/**
 * Signing in to a node started with development auth (`ACP_DEV_AUTH`, the
 * runtime config's `auth.kind: "dev"`): `POST /dev/token {userId}` mints a
 * token the node accepts. No Cognito, no password — which is exactly why a
 * production node never enables it (apps/state-service/src/devAuth.ts). The
 * e2e-local suite signs in this way, through the real login page.
 *
 * A dev token cannot be refreshed; when it expires (12 h) the user signs in
 * again.
 */
import type { FetchPort } from '@acp/api-client/ports.js';
import { z } from 'zod';
import { type Session, sessionFromTokens } from './session.js';

const devTokenResponseSchema = z.object({ token: z.string().min(1) }).loose();

export async function devLogin(fetch: FetchPort, apiUrl: string, userId: string): Promise<Session> {
  const response = await fetch(`${apiUrl}/dev/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify({ userId }),
  });
  const text = await response.text();
  if (response.status !== 200) {
    throw new Error(response.status === 404 ? 'this backend does not offer development sign-in' : `development sign-in failed (HTTP ${String(response.status)})`);
  }
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('development sign-in answered something that is not JSON');
  }
  const parsed = devTokenResponseSchema.safeParse(json);
  if (!parsed.success) throw new Error('development sign-in answered without a token');
  const session = sessionFromTokens('dev', parsed.data.token, null, null);
  if (session === null) throw new Error('development sign-in answered a token that is not a JWT');
  return session;
}

/** A dev session is never renewed. */
export async function refreshDevSession(): Promise<Session | null> {
  return null;
}
